import { CircleNotchIcon } from '@phosphor-icons/react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ProductSetConflict } from '@/modules/events/types';

interface ProductSetConflictsDialogProps {
  isOpen: boolean;
  isPending: boolean;
  conflicts: ProductSetConflict[];
  isEdit: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// A single sentence covers the three cases: several clashing products are
// summarised by count, a lone one names the other set when there is only one.
function getConflictsMessage(conflicts: ProductSetConflict[]) {
  if (conflicts.length > 1) {
    return `${conflicts.length} products in this set are already in other sets. You can still create the set.`;
  }

  const [conflict] = conflicts;

  if (!conflict) return '';

  return conflict.sets.length === 1
    ? `This product in size ${conflict.size} is already in “${conflict.sets[0].name}”.`
    : `This product in size ${conflict.size} is already in ${conflict.sets.length} other sets.`;
}

export function ProductSetConflictsDialog({
  isOpen,
  isPending,
  conflicts,
  isEdit,
  onConfirm,
  onCancel,
}: ProductSetConflictsDialogProps) {
  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent data-testid="product-set-conflicts-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {conflicts.length > 1
              ? 'Products already in other sets'
              : 'Product already in another set'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {getConflictsMessage(conflicts)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isPending}
            className="w-[140px]"
            data-testid="confirm-product-set-conflicts-button"
          >
            {isPending && <CircleNotchIcon className="animate-spin" />}
            {isEdit ? 'Save anyway' : 'Create anyway'}
          </AlertDialogAction>
          <AlertDialogCancel
            disabled={isPending}
            onClick={onCancel}
            data-testid="cancel-product-set-conflicts-button"
          >
            Cancel
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
